import { AppState } from '../../../redux/reducers';
import { useDispatch, useSelector, connect } from 'react-redux';
import React, { useEffect, useState } from 'react';
import { MedicalInsurance } from '../../../models/Entities/MedicalInsurances/MedicalInsurance';
import {
  DefaultButton,
  FontWeights,
  getTheme,
  IconButton,
  IIconProps,
  IStackProps,
  Label,
  mergeStyleSets,
  MessageBar,
  MessageBarType,
  Modal,
  PrimaryButton,
  Separator,
  Stack,
  TextField
} from '@fluentui/react';
import { NewMedicalInsurance } from '../../../models/Entities/MedicalInsurances/NewMedicalInsurance';
import { ErrorMessageComponent } from '../../Error/ErrorMessageComponent';

////i18Next
import { useTranslation, Trans } from 'react-i18next';

// store
import * as MedicalInsuranceStore from '../../../redux/middleware/MedicalInsuranceMiddleware';

interface FormValidation {
  success: boolean;
  errors: string[];
}

export interface IProps {
  isOpen: boolean;
  openPanel: () => void;
  dismissPanel: () => void;
  MedicalInsurance?: MedicalInsurance | undefined;
}

export const MedicalInsuranceFormComponent = (props: IProps) => {
  const [submittedForm, setSubmittedForm] = useState<boolean>(false);
  const [FormValidationStatus, setFormValidationStatus] = useState<
    FormValidation | undefined
  >();

  const MedicalInsuranceState = useSelector(
    (state: AppState) => state.MedicalInsurances
  );
  const dispatch = useDispatch();

  // MedicalInsurance state
  const [title, setTitle] = useState<string>();
  const [businessName, setBusinessName] = useState<string>();
  const [cuit, setCuit] = useState<string>();
  const [iibb, setIibb] = useState<string>();
  const [taxName, setTaxName] = useState<string>();

  //useTranslation()
  const [t] = useTranslation();

  // effects
  useEffect(() => {
    if (props.MedicalInsurance) {
      setTitle(props.MedicalInsurance.title);
      setBusinessName(props.MedicalInsurance.businessName);
      setCuit(props.MedicalInsurance.cuit);
      setIibb(props.MedicalInsurance.iibb);
      setTaxName(props.MedicalInsurance.taxName);
    } else {
      ResetForm();
    }
  }, [props.MedicalInsurance]);

  useEffect(() => {
    if (
      submittedForm &&
      MedicalInsuranceState &&
      (MedicalInsuranceState.successAddingNewOne ||
        MedicalInsuranceState.successUpdatingOne)
    ) {
      ResetForm();
      props.dismissPanel();
    }
  }, [
    MedicalInsuranceState?.successAddingNewOne,
    MedicalInsuranceState?.successUpdatingOne
  ]);

  const ResetForm = () => {
    setTitle(undefined);
    setBusinessName(undefined);
    setFormValidationStatus(undefined);
    setCuit(undefined);
    setIibb(undefined);
    setTaxName(undefined);
    setSubmittedForm(false);
  };

  // Events

  const onChangeTitleTextFieldValue = (
    event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string
  ) => {
    setTitle(newValue || '');
  };

  const onChangeBusinessNameTextFieldValue = (
    event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string
  ) => {
    setBusinessName(newValue || '');
  };

  const onChangeCuitTextFieldValue = (
    event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string
  ) => {
    setCuit(newValue || '');
  };

  const onChangeIibbTextFieldValue = (
    event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string
  ) => {
    setIibb(newValue || '');
  };

  const onChangeTaxNameTextFieldValue = (
    event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string
  ) => {
    setTaxName(newValue || '');
  };

  const HandleSaveForm = () => {
    setSubmittedForm(true);
    if (ValidateForm()) {
      var CurrentValues: MedicalInsurance | NewMedicalInsurance;
      if (props.MedicalInsurance) {
        CurrentValues = {
          id: props.MedicalInsurance.id,
          title: title!,
          businessName: businessName!,
          cuit: cuit!,
          iibb: iibb!,
          taxName: taxName!
        };
        dispatch(
          mapDispatchToProps.UpdateMedicalInsurance(
            CurrentValues as MedicalInsurance
          )
        );
      } else {
        CurrentValues = {
          title: title!,
          businessName: businessName!,
          cuit: cuit!,
          iibb: iibb!,
          taxName: taxName!
        };
        dispatch(mapDispatchToProps.AddMedicalInsurance(CurrentValues));
      }
    }
  };

  const HandleCancelForm = () => {
    ResetForm();

    props.dismissPanel();
  };

  // footer
  const onRenderFooterContent = () => {
    return (
      <div className={contentStyles.footer}>
        <DefaultButton onClick={HandleCancelForm} className="OtherOption">
          <Trans i18nKey={'medicalInsuranceForm.footer.cancelButton'}/>
        </DefaultButton>
        <PrimaryButton
          onClick={HandleSaveForm}
          styles={buttonStyles}
          className="ConfirmAction"
        >
          <Trans i18nKey={'medicalInsuranceForm.footer.saveButton'}/>
        </PrimaryButton>
      </div>
    );
  };

  const formFields = (
    <Stack {...columnProps}>
      <TextField
        label={t('medicalInsuranceForm.props.tittle')} 
        name="txt_title"
        required
        autoComplete="off"
        onChange={onChangeTitleTextFieldValue}
        errorMessage={
          (submittedForm === true && title === undefined) ||
          (submittedForm === true && title !== undefined && title.length === 0)
            ? t('medicalInsuranceForm.props.requiredField')
            : undefined
        }
        value={title || ''}
      />
      <TextField
        label={t('medicalInsuranceForm.props.initials')} 
        name="txt_businessName"
        required
        onChange={onChangeBusinessNameTextFieldValue}
        autoComplete="off"
        rows={1}
        value={businessName || ''}
        errorMessage={
          (submittedForm === true && businessName === undefined) ||
          (submittedForm === true &&
            businessName !== undefined &&
            businessName.length === 0)
            ? t('medicalInsuranceForm.props.requiredField')
            : undefined
        }
      />
      <Separator />
      <Label>{t('medicalInsuranceForm.props.taxInformation')}</Label>
      <TextField
        label={t('medicalInsuranceForm.props.taxName')} 
        name="txt_taxName"
        required
        onChange={onChangeTaxNameTextFieldValue}
        autoComplete="off"
        rows={1}
        value={taxName || ''}
        errorMessage={
          (submittedForm === true && taxName === undefined) ||
          (submittedForm === true &&
            taxName !== undefined &&
            taxName.length === 0)
            ? t('medicalInsuranceForm.props.requiredField')
            : undefined
        }
      />
      <TextField
        label={t('medicalInsuranceForm.props.cuit')}
        name="txt_cuit"
        type="number"
        required
        onChange={onChangeCuitTextFieldValue}
        autoComplete="off"
        rows={1}
        value={cuit || ''}
        errorMessage={
          (submittedForm === true && cuit === undefined) ||
          (submittedForm === true && cuit !== undefined && cuit.length === 0)
            ? t('medicalInsuranceForm.props.requiredField')
            : undefined
        }
      />
      <TextField
        label={t('medicalInsuranceForm.props.iibb')}
        name="txt_iibb"
        type="number"
        required
        onChange={onChangeIibbTextFieldValue}
        autoComplete="off"
        rows={1}
        value={iibb || ''}
        errorMessage={
          (submittedForm === true && iibb === undefined) ||
          (submittedForm === true && iibb !== undefined && iibb.length === 0)
            ? t('medicalInsuranceForm.props.requiredField')
            : undefined
        }
      />
    </Stack>
  );

  // form validation

  const ValidateForm = (): boolean => {
    var validationResult: boolean = true;

    var FormValidation: FormValidation = { success: true, errors: [] };
    setFormValidationStatus(FormValidation);

    if (title === undefined || title.length === 0) {
      validationResult = false;
      FormValidation.errors.push(
        t('medicalInsuranceForm.formValidation.informName')
      );
    }

    if (businessName === undefined || businessName.length === 0) {
      validationResult = false;
      FormValidation.errors.push(t('medicalInsuranceForm.formValidation.informInitials'));
    }

    if (taxName === undefined || taxName.length === 0) {
      validationResult = false;
      FormValidation.errors.push(t('medicalInsuranceForm.formValidation.taxNameInformation'));
    }

    if (cuit === undefined || cuit.length === 0) {
      validationResult = false;
      FormValidation.errors.push(t('medicalInsuranceForm.formValidation.cuitInformation'));
    }

    if (iibb === undefined || iibb.length === 0) {
      validationResult = false;
      FormValidation.errors.push(t('medicalInsuranceForm.formValidation.iibbInformation'));
    }

    FormValidation.success = validationResult;
    setFormValidationStatus(FormValidation);
    return validationResult;
  };

  return (
    <Modal
      isOpen={props.isOpen}
      onDismiss={props.dismissPanel}
      isModeless={false}
      containerClassName={contentStyles.container}
      closeButtonAriaLabel="Close"
      //onRenderFooterContent={onRenderFooterContent}
      // Stretch panel content to fill the available height so the footer is positioned
      // at the bottom of the page
      //isFooterAtBottom={true}
    >
      <div className={contentStyles.header}>
        <span><Trans i18nKey={'medicalInsuranceForm.modal.medicalCoverage'}/></span>
        <IconButton
          styles={iconButtonStyles}
          iconProps={cancelIcon}
          ariaLabel="Close popup modal"
          onClick={props.dismissPanel}
        />
      </div>
      {submittedForm && !FormValidationStatus?.success && (
        <MessageBar messageBarType={MessageBarType.error} isMultiline={true}>
          {t('medicalInsuranceForm.modal.reviewInformation')}
          <ul>
            {FormValidationStatus?.errors.map(
              (Error: string, index: number) => (
                <li key={index}>{Error}</li>
              )
            )}
          </ul>
        </MessageBar>
      )}
      {submittedForm &&
        MedicalInsuranceState &&
        (MedicalInsuranceState.failOnAddingNewOne ||
          MedicalInsuranceState.failOnUpdatingOne) &&
        MedicalInsuranceState.error && (
          <ErrorMessageComponent Error={MedicalInsuranceState.error} />
        )}
      <div className={contentStyles.body}>{formFields}</div>
      {onRenderFooterContent()}
    </Modal>
  );
};

const mapStateToProps = (state: AppState) => ({
  ...state.MedicalInsurances
});

const mapDispatchToProps = {
  ...MedicalInsuranceStore.actionCreators
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(MedicalInsuranceFormComponent as any);

const buttonStyles = { root: { marginRight: 8, class: 'ConfirmAction' } };

const columnProps: Partial<IStackProps> = {
  tokens: { childrenGap: 15 }
  // styles: { root: { width: 150 } }
};

const theme = getTheme();
const contentStyles = mergeStyleSets({
  container: {
    display: 'flex',
    flexFlow: 'column nowrap',
    alignItems: 'stretch',
    borderRadius: 10
  },
  header: [
    theme.fonts.mediumPlus,
    {
      flex: '1 1 auto',
      color: 'var(--ichtys-text)',
      display: 'flex',
      alignItems: 'center',
      fontWeight: FontWeights.semibold,
      padding: '0px 12px 12px 12px',
      backgroundColor: 'var(--ichtys-bg)',
      borderRadius: '10px 10px 0 0'
    }
  ],
  body: {
    flex: '4 4 auto',
    padding: '0 24px 24px 24px',
    overflowY: 'hidden',
    selectors: {
      p: { margin: '14px 0' },
      'p:first-child': { marginTop: 0 },
      'p:last-child': { marginBottom: 0 }
    }
  },
  footer: {
    flex: '4 4 auto',
    padding: '0 24px 24px 24px'
  }
});

const cancelIcon: IIconProps = { iconName: 'Cancel' };

const iconButtonStyles = {
  root: {
    color: 'var(--ichtys-text)',
    marginLeft: 'auto',
    marginTop: '4px',
    marginRight: '2px'
  },
  rootHovered: {
    color: 'var(--ichtys-text)'
  }
};
